import { useState } from 'react';
import {
  Table, Group, Text, Badge, Select, Modal, Pagination, SimpleGrid,
  ActionIcon, Tooltip, Stack, Box,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDebouncedValue } from '@mantine/hooks';
import { IconHistory, IconListDetails } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { EmptyState } from './EmptyState';
import { FetchOverlay } from './FetchOverlay';
import { StatCard } from './StatCard';
import { TableSkeleton } from './TableSkeleton';
import { TableToolbar } from './TableToolbar';
import { formatDate, formatDateTime } from '../lib/format';
import { SectionCard } from './SectionCard';

/** Tanlov ro'yxati elementi (sinf/fan/o'qituvchi) */
export interface Opt { value: string; label: string }

interface Person { id: string; fullName: string }
interface ExamClass { id: string; name: string }

/** GET /ranking/exams/history — audit yig'indisi (kim nechta ball kiritgan) */
interface AuditPerson { id: string; fullName: string; count: number; lastAt: string | null }

/** Tarix elementi = umumiy imtihon obyekti + stats + audit */
interface HistoryExam {
  id: string;
  title: string;
  date: string;
  maxScore: number;
  subject: { id: string; name: string } | null;
  classes: ExamClass[];
  teacher: Person | null;
  createdBy: Person | null;
  _count: { scores: number };
  stats: {
    students: number | null;
    scored: number;
    avgScore: number | null;
    avgPercent: number | null;
    bestScore: number | null;
    worstScore: number | null;
    lastEntryAt: string | null;
  };
  audit: { enteredBy: AuditPerson[]; editedBy: AuditPerson[] };
}

/** GET /ranking/exams/:id/audit — bitta ball qatorining tarixi */
interface AuditRow {
  id: string;
  score: number;
  percent: number | null;
  comment: string | null;
  createdAt: string;
  updatedAt: string;
  student: { id: string; fullName: string; class: ExamClass | null };
  createdBy: Person | null;
  updatedBy: Person | null;
}

/** Lokal sana → "YYYY-MM-DD" (toISOString() EMAS: UTC+5 da kun siljirdi). */
const iso = (d: Date | null) =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : undefined;

/**
 * T8 — TEST IMTIHONLAR TARIXI.
 *
 * Ro'yxat sahifasidan farqi: bu yerda imtihonning NATIJASI (nechta ball
 * qo'yilgan, o'rtacha/eng yuqori/eng past) va BALL KIRITISH AUDITI ko'rinadi —
 * "kim kiritgan" va "kim keyin o'zgartirgan". Hisob-kitobning hammasini backend
 * qiladi (`/ranking/exams/history`), front faqat ko'rsatadi.
 */
export function ExamHistory({
  classOptions, subjectOptions, teacherOptions,
}: {
  classOptions: Opt[];
  subjectOptions: Opt[];
  teacherOptions: Opt[];
}) {
  const { t } = useTranslation();

  const [search, setSearch] = useState('');
  const [qDeb] = useDebouncedValue(search, 300);
  const [classF, setClassF] = useState<string | null>(null);
  const [subjectF, setSubjectF] = useState<string | null>(null);
  const [teacherF, setTeacherF] = useState<string | null>(null);
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null]);
  const [ungradedF, setUngradedF] = useState<string | null>(null);
  const [sort, setSort] = useState('date:desc');

  const { items, isLoading, fetching, pages, page, setPage, data } = usePagedQuery<HistoryExam>(
    'exam-history',
    '/ranking/exams/history',
    {
      q: qDeb || undefined,
      classId: classF || undefined,
      subjectId: subjectF || undefined,
      teacherId: teacherF || undefined,
      from: iso(range[0]),
      to: iso(range[1]),
      ungraded: ungradedF || undefined,
      sort,
    },
    20,
  );
  // `summary` — sahifalangan konvertdan TASHQARIDAGI maydon (butun filtr bo'yicha).
  // Backend juda katta tanlovda uni `null` qaytaradi — o'shanda kartalar ko'rsatilmaydi.
  const summary = (data as unknown as { summary?: { exams: number; scores: number; avgPercent: number | null } | null } | undefined)?.summary;

  const [auditExam, setAuditExam] = useState<HistoryExam | null>(null);

  /** "Kim kiritdi / kim o'zgartirdi" — birinchi ism + qolganlari tooltipda. */
  const people = (list: AuditPerson[]) => {
    if (!list.length) return <Text fz="sm" c="dimmed">—</Text>;
    const label = list.length > 1 ? `${list[0].fullName} +${list.length - 1}` : list[0].fullName;
    return (
      <Tooltip
        withArrow
        multiline
        w={260}
        label={list.map((p) => `${p.fullName} — ${t('common.itemsCount', { count: p.count })}`).join(' · ')}
      >
        <Text fz="sm" lineClamp={1}>{label}</Text>
      </Tooltip>
    );
  };

  /** Imtihon sinflari — 2 tadan ko'p bo'lsa qolganlari "+N" bo'lib yig'iladi. */
  const classCell = (list: ExamClass[]) => {
    if (!list.length) return <Text fz="sm" c="dimmed">{t('examsPage.allSchool')}</Text>;
    const shown = list.slice(0, 2);
    return (
      <Group gap={4} wrap="nowrap">
        {shown.map((c) => <Badge key={c.id} variant="light" radius="sm">{c.name}</Badge>)}
        {list.length > shown.length && (
          <Tooltip withArrow label={list.map((c) => c.name).join(', ')}>
            <Badge variant="default" radius="sm">+{list.length - shown.length}</Badge>
          </Tooltip>
        )}
      </Group>
    );
  };

  return (
    <>
      {summary && (
        <SimpleGrid cols={{ base: 1, sm: 3 }} mb="md">
          <StatCard icon={IconHistory} color="brand" label={t('examHistory.exams')} value={summary.exams} />
          <StatCard icon={IconListDetails} color="teal" label={t('examHistory.scores')} value={summary.scores} />
          <StatCard
            icon={IconListDetails}
            color="grape"
            label={t('examHistory.avgPercent')}
            value={summary.avgPercent != null ? `${summary.avgPercent}%` : '—'}
          />
        </SimpleGrid>
      )}

      <TableToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder={t('examsPage.searchPlaceholder')}
        sort={sort}
        onSort={setSort}
        sortOptions={[
          { value: 'date:desc', label: t('examsPage.sortDateDesc') },
          { value: 'date:asc', label: t('examsPage.sortDateAsc') },
          { value: 'createdAt:desc', label: t('examHistory.sortCreatedDesc') },
        ]}
      >
        <DatePickerInput
          type="range"
          placeholder={t('examsPage.dateRange')}
          value={range}
          onChange={setRange}
          valueFormat="DD.MM.YYYY"
          clearable
          w={200}
        />
        <Select
          placeholder={t('examsPage.allClasses')}
          data={classOptions}
          value={classF}
          onChange={setClassF}
          clearable searchable w={160}
        />
        <Select
          placeholder={t('examsPage.allSubjects')}
          data={subjectOptions}
          value={subjectF}
          onChange={setSubjectF}
          clearable searchable w={160}
        />
        {teacherOptions.length > 0 && (
          <Select
            placeholder={t('examsPage.allTeachers')}
            data={teacherOptions}
            value={teacherF}
            onChange={setTeacherF}
            clearable searchable w={180}
          />
        )}
        <Select
          placeholder={t('examsPage.allExams')}
          data={[{ value: '1', label: t('examsPage.ungraded') }]}
          value={ungradedF}
          onChange={setUngradedF}
          clearable w={180}
        />
      </TableToolbar>

      <SectionCard title={t('common.list')} icon={IconHistory} p={0} pos="relative">
        {isLoading ? (
          <TableSkeleton rows={6} cols={7} />
        ) : !items.length ? (
          <EmptyState icon={IconHistory} title={t('examHistory.empty')} description={t('common.noMatch')} />
        ) : (
          <Table.ScrollContainer minWidth={1050}>
            <FetchOverlay visible={fetching} />
            <Table highlightOnHover verticalSpacing="sm" stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('examsPage.examTitle')}</Table.Th>
                  <Table.Th>{t('examsPage.class')}</Table.Th>
                  <Table.Th>{t('examsPage.teacher')}</Table.Th>
                  <Table.Th>{t('examsPage.date')}</Table.Th>
                  <Table.Th>{t('examHistory.scored')}</Table.Th>
                  <Table.Th>{t('examHistory.result')}</Table.Th>
                  <Table.Th>{t('examHistory.enteredBy')}</Table.Th>
                  <Table.Th>{t('examHistory.editedBy')}</Table.Th>
                  <Table.Th>{t('common.actions')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((e) => (
                  <Table.Tr key={e.id}>
                    <Table.Td>
                      <Text fw={500} fz="sm" lineClamp={1}>{e.title}</Text>
                      <Text fz="xs" c="dimmed">{e.subject?.name ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>{classCell(e.classes)}</Table.Td>
                    <Table.Td><Text fz="sm">{e.teacher?.fullName ?? '—'}</Text></Table.Td>
                    <Table.Td>{formatDate(e.date)}</Table.Td>
                    <Table.Td>
                      {/* Qamrov: nechta o'quvchidan nechtasiga ball qo'yilgan.
                          `students` null — umummaktab imtihoni (sinf noma'lum). */}
                      <Text fz="sm" fw={500}>
                        {e.stats.students != null ? `${e.stats.scored} / ${e.stats.students}` : e.stats.scored}
                      </Text>
                      {e.stats.lastEntryAt && (
                        <Text fz="xs" c="dimmed">{formatDateTime(e.stats.lastEntryAt)}</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {e.stats.avgScore != null ? (
                        <>
                          <Text fz="sm" fw={500}>
                            {e.stats.avgScore} / {e.maxScore}
                            {e.stats.avgPercent != null ? ` · ${e.stats.avgPercent}%` : ''}
                          </Text>
                          <Text fz="xs" c="dimmed">
                            {t('examHistory.best')}: {e.stats.bestScore} · {t('examHistory.worst')}: {e.stats.worstScore}
                          </Text>
                        </>
                      ) : (
                        <Text fz="sm" c="dimmed">{t('examHistory.noScores')}</Text>
                      )}
                    </Table.Td>
                    <Table.Td>{people(e.audit.enteredBy)}</Table.Td>
                    <Table.Td>{people(e.audit.editedBy)}</Table.Td>
                    <Table.Td>
                      <Tooltip label={t('examHistory.openAudit')} withArrow>
                        <ActionIcon
                          variant="light"
                          disabled={!e.stats.scored}
                          onClick={() => setAuditExam(e)}
                          aria-label={t('examHistory.openAudit')}
                        >
                          <IconListDetails size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>

      {pages > 1 && (
        <Group justify="center" mt="md">
          <Pagination value={page} onChange={setPage} total={pages} />
        </Group>
      )}

      <ExamAuditModal exam={auditExam} onClose={() => setAuditExam(null)} />
    </>
  );
}

/**
 * Bitta imtihon bo'yicha ball kiritish auditi: har bir ball qatorida kim
 * BIRINCHI kiritgan va kim OXIRGI o'zgartirgan (backend `/exams/:id/audit`).
 */
function ExamAuditModal({ exam, onClose }: { exam: HistoryExam | null; onClose: () => void }) {
  const { t } = useTranslation();
  const [classF, setClassF] = useState<string | null>(null);
  const [sort, setSort] = useState('updatedAt:desc');

  // `examId` ATAYLAB params ichida: `usePagedQuery` kesh kalitiga ENDPOINT emas,
  // faqat (key, page, limit, params) kiradi — imtihon almashganda eski imtihonning
  // qatorlari keshdan ko'rinib qolardi. Params o'zgargani sahifani ham 1 ga qaytaradi.
  // Backend bu parametrni o'qimaydi (yo'l `/exams/:id/audit`), ya'ni zararsiz.
  const { items, isLoading, fetching, pages, page, setPage } = usePagedQuery<AuditRow>(
    'exam-audit',
    `/ranking/exams/${exam?.id ?? 'none'}/audit`,
    { examId: exam?.id, classId: classF || undefined, sort },
    50,
    !!exam,
  );

  // Modal yopilganda filtrlar tozalansin — keyingi imtihon boshqa sinfga tegishli
  // bo'lishi mumkin, eski `classId` esa bo'sh ro'yxat berardi.
  const close = () => {
    setClassF(null);
    setSort('updatedAt:desc');
    onClose();
  };

  return (
    <Modal
      opened={!!exam}
      onClose={close}
      size="xl"
      centered
      title={
        <div>
          <Text fw={700}>{t('examHistory.auditTitle')}</Text>
          {exam ? <Text fz="xs" c="dimmed">{exam.title}</Text> : null}
        </div>
      }
    >
      <Group gap="sm" mb="md" align="flex-end" wrap="wrap">
        {(exam?.classes.length ?? 0) > 1 && (
          <Select
            placeholder={t('examsPage.allClasses')}
            data={(exam?.classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
            value={classF}
            onChange={setClassF}
            clearable
            w={170}
          />
        )}
        <Select
          data={[
            { value: 'updatedAt:desc', label: t('examHistory.sortUpdatedDesc') },
            { value: 'createdAt:desc', label: t('examHistory.sortEnteredDesc') },
            { value: 'score:desc', label: t('examHistory.sortScoreDesc') },
            { value: 'score:asc', label: t('examHistory.sortScoreAsc') },
          ]}
          value={sort}
          onChange={(v) => v && setSort(v)}
          allowDeselect={false}
          w={220}
        />
      </Group>

      {isLoading ? (
        <TableSkeleton rows={5} cols={5} />
      ) : !items.length ? (
        <EmptyState icon={IconListDetails} title={t('examHistory.noScores')} height={160} />
      ) : (
        <Box pos="relative">
          <FetchOverlay visible={fetching} />
          <Table.ScrollContainer minWidth={760}>
            <Table verticalSpacing="sm" striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('examsPage.student')}</Table.Th>
                  <Table.Th>{t('examsPage.score')}</Table.Th>
                  <Table.Th>{t('examHistory.enteredBy')}</Table.Th>
                  <Table.Th>{t('examHistory.editedBy')}</Table.Th>
                  <Table.Th>{t('examsPage.comment')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((r) => (
                  <Table.Tr key={r.id}>
                    <Table.Td>
                      <Text fz="sm" fw={500}>{r.student.fullName}</Text>
                      {r.student.class && <Text fz="xs" c="dimmed">{r.student.class.name}</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm" fw={600}>
                        {r.score}{exam ? ` / ${exam.maxScore}` : ''}
                      </Text>
                      {r.percent != null && <Text fz="xs" c="dimmed">{r.percent}%</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text fz="sm">{r.createdBy?.fullName ?? '—'}</Text>
                        <Text fz="xs" c="dimmed">{formatDateTime(r.createdAt)}</Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      {r.updatedBy ? (
                        <Stack gap={0}>
                          <Text fz="sm">{r.updatedBy.fullName}</Text>
                          <Text fz="xs" c="dimmed">{formatDateTime(r.updatedAt)}</Text>
                        </Stack>
                      ) : (
                        <Text fz="sm" c="dimmed">{t('examHistory.notEdited')}</Text>
                      )}
                    </Table.Td>
                    <Table.Td><Text fz="sm" lineClamp={2} maw={200}>{r.comment || '—'}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Box>
      )}

      {pages > 1 && (
        <Group justify="center" mt="md">
          <Pagination value={page} onChange={setPage} total={pages} size="sm" />
        </Group>
      )}
    </Modal>
  );
}
